import { lineTotal, quotePriceError } from "@/lib/quoteNumbers";
// Building blocks for the full-page quote workspace.
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Building2, ChevronDown, ChevronRight, Copy, FileText, GripVertical, Layers, Loader2,
  MoreHorizontal, Paperclip, Plus, Search, Send, Trash2, UploadCloud, X, Zap,
  Bold, Italic, List, ListOrdered, Link2, Briefcase, Hammer, Package, Truck, Wrench, Star,
  StickyNote, CircleDollarSign, Percent, ScrollText, Settings2, Activity as ActivityIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const nzd = (n) => new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n || 0);
const inputCls = "h-8 rounded-sm border-zinc-300 dark:border-white/15 dark:bg-white/5 text-sm";
const labelCls = "text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400";

export function SectionCard({ title, icon: Icon, right, children, className = "", testid, pad = true }) {
  return (
    <section className={`border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 dark:border-white/5">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400">
          {Icon && <Icon className="w-3.5 h-3.5 text-[#FF5F15]" />} {title}
        </div>
        {right}
      </div>
      <div className={pad ? "p-4" : ""}>{children}</div>
    </section>
  );
}

// ==================== CUSTOMER ====================
export function CustomerSection({ q, patch, clients, setClients, editable, title = "Customer" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", email: "", phone: "", address: "" });
  const filtered = clients.filter((c) => (c.name || "").toLowerCase().includes(search.toLowerCase()));

  const pick = (c) => {
    patch({ client_id: c.id, client_name: c.name, client_email: c.email || "", client_address: c.address || "" });
    setOpen(false);
  };
  const addClient = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/clients", form);
      setClients([data, ...clients]);
      pick(data);
      setAdding(false);
      setForm({ name: "", contact_person: "", email: "", phone: "", address: "" });
    } catch { toast.error("Could not add customer"); }
  };
  const selected = clients.find((c) => c.id === q.client_id) ||
    (q.client_name ? { name: q.client_name, email: q.client_email, address: q.client_address } : null);

  return (
    <SectionCard title={title} icon={Building2} testid="quote-customer-section"
      right={editable && selected && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline inline-flex items-center gap-1" data-testid="quote-change-customer">
              {selected ? "Change Customer" : "Select Customer"} <ChevronDown className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0 rounded-sm" data-testid="quote-customer-picker">
            {adding ? (
              <form onSubmit={addClient} className="p-3 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">New customer</div>
                <Input required placeholder="Company / customer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="new-customer-name" />
                <Input placeholder="Contact person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={inputCls} />
                <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="new-customer-email" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
                <div className="flex gap-2 justify-end pt-1">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)} className="h-7 text-[10px] font-bold uppercase">Cancel</Button>
                  <Button type="submit" size="sm" className="h-7 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase" data-testid="new-customer-save">Save</Button>
                </div>
              </form>
            ) : (
              <>
                <div className="p-2 border-b border-zinc-100 dark:border-white/5 relative">
                  <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input autoFocus placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-8`} data-testid="customer-search" />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {filtered.map((c) => (
                    <button key={c.id} onClick={() => pick(c)} className="w-full text-left px-3 py-2 hover:bg-zinc-50 dark:hover:bg-white/5 text-sm" data-testid={`customer-option-${c.id}`}>
                      <div className="font-bold">{c.name}</div>
                      {c.email && <div className="text-[11px] text-zinc-500">{c.email}</div>}
                    </button>
                  ))}
                  {filtered.length === 0 && <div className="px-3 py-4 text-xs text-zinc-400 text-center">No matches</div>}
                </div>
                <button onClick={() => setAdding(true)} className="w-full px-3 py-2.5 border-t border-zinc-100 dark:border-white/5 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] hover:bg-orange-50 dark:hover:bg-white/5 text-left" data-testid="add-new-customer">
                  + Add New Customer
                </button>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}>
      {selected ? (
        <div className="flex items-start gap-3.5" data-testid="quote-customer-summary">
          <div className="w-10 h-10 bg-[#FF5F15]/10 border border-[#FF5F15]/30 flex items-center justify-center shrink-0">
            <Building2 className="w-4.5 h-4.5 text-[#FF5F15]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 min-w-0 flex-1">
            <div className="sm:col-span-2">
              <div className="font-bold text-base">{selected.name}</div>
              {selected.address && <div className="text-xs text-zinc-500 mt-0.5 whitespace-pre-line">{selected.address}</div>}
            </div>
            {selected.contact_person && (
              <div><div className={labelCls}>Contact Person</div><div className="text-sm mt-0.5">{selected.contact_person}</div></div>
            )}
            {selected.email && (
              <div><div className={labelCls}>Email</div><div className="text-sm mt-0.5">{selected.email}</div></div>
            )}
            {selected.phone && (
              <div><div className={labelCls}>Phone</div><div className="text-sm mt-0.5">{selected.phone}</div></div>
            )}
          </div>
        </div>
      ) : editable ? (
        <div data-testid="quote-no-customer" className="border-2 border-dashed border-zinc-300 dark:border-white/10 p-4 sm:p-5">
          {adding ? (
            <form onSubmit={addClient} className="grid sm:grid-cols-2 gap-2 max-w-2xl">
              <div className="sm:col-span-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">New customer</div>
              <Input required placeholder="Company / customer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="new-customer-name" />
              <Input placeholder="Contact person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={inputCls} />
              <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="new-customer-email" />
              <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${inputCls} sm:col-span-2`} />
              <div className="sm:col-span-2 flex gap-2 justify-end pt-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)} className="h-8 text-[10px] font-bold uppercase">Cancel</Button>
                <Button type="submit" size="sm" className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase" data-testid="new-customer-save">Save customer</Button>
              </div>
            </form>
          ) : (
            <>
              <div className="relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input placeholder="Search customers by name…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9 h-10`} data-testid="customer-search" />
              </div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {filtered.slice(0, 9).map((c) => (
                  <button key={c.id} onClick={() => pick(c)} className="text-left px-3 py-2 border border-zinc-200 dark:border-white/10 rounded-sm hover:border-[#FF5F15] hover:bg-[#FF5F15]/5 transition-colors" data-testid={`customer-option-${c.id}`}>
                    <div className="font-bold text-sm truncate">{c.name}</div>
                    {c.email && <div className="text-[11px] text-zinc-500 truncate">{c.email}</div>}
                  </button>
                ))}
                {filtered.length === 0 && <div className="col-span-full text-xs text-zinc-400 py-3">No matches — add a new customer below.</div>}
              </div>
              {filtered.length > 9 && <div className="mt-1.5 text-[11px] text-zinc-400">Showing 9 of {filtered.length} — keep typing to narrow it down.</div>}
              <button onClick={() => setAdding(true)} className="mt-3 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid="add-new-customer">+ Add New Customer</button>
            </>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed border-zinc-300 dark:border-white/10 p-6 text-center text-sm text-zinc-400" data-testid="quote-no-customer-readonly">
          No customer selected.
        </div>
      )}
    </SectionCard>
  );
}

// ==================== QUOTE DETAILS ====================
export function QuoteDetailsSection({ q, patch, validDays, advancedOpen, setAdvancedOpen }) {
  const adv = q.advanced || {};
  const setAdv = (k) => (e) => patch({ advanced: { ...adv, [k]: e.target.value } });
  return (
    <SectionCard title="Quote Details" icon={FileText} testid="quote-details-section">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className={labelCls}>Quote Number</Label>
          <Input value={q.quote_number || ""} readOnly className={`${inputCls} mt-1.5 bg-zinc-50 dark:bg-white/[0.03] font-mono font-bold`} data-testid="quote-number" />
        </div>
        <div>
          <Label className={labelCls}>Issue Date</Label>
          <Input type="date" value={q.issue_date || ""} onChange={(e) => patch({ issue_date: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="quote-issue-date" />
        </div>
        <div>
          <Label className={labelCls}>Valid Until</Label>
          <Input type="date" value={q.valid_until || ""} onChange={(e) => patch({ valid_until: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="quote-valid-until" />
          {validDays != null && <div className="text-[10px] text-zinc-400 mt-1 font-mono" data-testid="quote-valid-days">{validDays} days</div>}
        </div>
        <div>
          <Label className={labelCls}>Reference</Label>
          <Input value={q.reference || ""} onChange={(e) => patch({ reference: e.target.value })} placeholder="e.g. Harbour View Subdivision" className={`${inputCls} mt-1.5`} data-testid="quote-reference" />
        </div>
      </div>
      <div className="mt-3">
        <Label className={labelCls}>Quote Title</Label>
        <Input value={q.title || ""} onChange={(e) => patch({ title: e.target.value })} placeholder="e.g. Harbour View Subdivision – Stage 1 Drainage Works" className={`${inputCls} mt-1.5`} data-testid="quote-title" />
      </div>
      <div className="mt-3">
        <Label className={labelCls}>Short Description (shown under the title on the customer proposal)</Label>
        <Input value={q.description || ""} onChange={(e) => patch({ description: e.target.value })} placeholder="e.g. Supply and install soak hole with clean boulders and geotextile." className={`${inputCls} mt-1.5`} data-testid="quote-description" />
      </div>
      <button onClick={() => setAdvancedOpen(!advancedOpen)} className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15]" data-testid="quote-advanced-toggle">
        <ChevronRight className={`w-3 h-3 transition-transform ${advancedOpen ? "rotate-90" : ""}`} /> Advanced Details
      </button>
      {advancedOpen && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pt-3 border-t border-zinc-100 dark:border-white/5" data-testid="quote-advanced-fields">
          {[["project_manager", "Project Manager"], ["salesperson", "Salesperson"], ["cost_centre", "Cost Centre"],
            ["po_number", "Purchase Order Number"], ["internal_ref", "Internal Reference"]].map(([k, label]) => (
            <div key={k}>
              <Label className={labelCls}>{label}</Label>
              <Input value={adv[k] || ""} onChange={setAdv(k)} className={`${inputCls} mt-1.5`} data-testid={`quote-adv-${k}`} />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ==================== LINE ITEMS TABLE ====================
function AutoGrowTextarea({ value, onChange, className = "", ...rest }) {
  const ref = useRef(null);
  const resize = () => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = Math.max(el.scrollHeight, 38) + "px"; } };
  useEffect(() => { resize(); }, [value]);
  return <Textarea ref={ref} rows={1} value={value}
    onChange={(e) => { onChange(e); resize(); }}
    className={`resize-none overflow-hidden leading-snug whitespace-pre-wrap break-words ${className}`} {...rest} />;
}

export function LineItemsTable({ items, updateItem, removeItem, duplicateItem, moveItem, expandedRow, setExpandedRow, sectionSubtotals, settings, dragRef, onDrop, editable }) {
  const cellIn = "h-9 rounded-sm border-zinc-200 dark:border-white/10 dark:bg-white/5 text-sm px-2";
  const [dragOver, setDragOver] = useState(null);
  const dropHandlers = (i) => ({
    draggable: editable,
    onDragStart: () => { dragRef.current = i; },
    onDragOver: (e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); },
    onDragEnd: () => setDragOver(null),
    onDrop: () => { onDrop(i); setDragOver(null); },
  });
  const dropLine = (i) => (dragOver === i && dragRef.current !== i ? " shadow-[inset_0_3px_0_0_#FF5F15]" : "");

  // hide rows inside collapsed sections
  let collapsed = false;
  const rows = items.map((it, i) => {
    if (it.is_section) { collapsed = !!it.collapsed; return { it, i, hidden: false }; }
    return { it, i, hidden: collapsed };
  });
  let n = 0;

  if (!items.length) {
    return (
      <div className="p-8 text-center text-sm text-zinc-400" data-testid="quote-items-empty">
        No line items yet — hit <b>+ Add Item</b> or pull from the <b>Library</b>.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="text-left text-[9px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
            <th className="w-8" />
            <th className="px-1.5 py-2 w-8">#</th>
            <th className="px-1.5 py-2">Description</th>
            <th className="px-1.5 py-2 w-20 text-right">Qty</th>
            <th className="px-1.5 py-2 w-16">Unit</th>
            <th className="px-1.5 py-2 w-28 text-right">Unit Price</th>
            <th className="px-1.5 py-2 w-20 text-right">Disc %</th>
            <th className="px-1.5 py-2 w-28 text-right">Total</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ it, i, hidden }) => {
            if (hidden) return null;
            if (it.is_section) {
              return (
                <tr key={i} className={`bg-zinc-100 dark:bg-white/[0.06] border-b border-zinc-200 dark:border-white/10${dropLine(i)}`}
                    {...dropHandlers(i)}
                    data-testid={`quote-section-row-${i}`}>
                  <td className="pl-2 cursor-grab text-zinc-400"><GripVertical className="w-3.5 h-3.5" /></td>
                  <td colSpan={2} className="px-1.5 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => updateItem(i, { collapsed: !it.collapsed })} className="text-zinc-500" data-testid={`section-collapse-${i}`}>
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${it.collapsed ? "" : "rotate-90"}`} />
                      </button>
                      <Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })}
                        className="h-7 rounded-sm border-transparent bg-transparent font-bold uppercase tracking-wider text-xs w-64 focus:border-zinc-300"
                        data-testid={`section-name-${i}`} disabled={!editable} />
                    </div>
                  </td>
                  <td colSpan={4} />
                  <td className="px-1.5 py-2 text-right font-mono font-bold text-xs" data-testid={`section-subtotal-${i}`}>
                    {settings?.show_section_subtotals !== false ? nzd(sectionSubtotals[i] || 0) : ""}
                  </td>
                  <td className="pr-2 text-right">
                    {editable && <RowMenu i={i} isSection onDup={() => duplicateItem(i)} onUp={() => moveItem(i, -1)} onDown={() => moveItem(i, 1)} onDel={() => removeItem(i)} onEdit={() => {}} />}
                  </td>
                </tr>
              );
            }
            n += 1;
            const expanded = expandedRow === i;
            return (
              <RowGroup key={i}>
                <tr className={`border-b border-zinc-100 dark:border-white/5 hover:bg-zinc-50 dark:hover:bg-white/[0.02] ${expanded ? "bg-orange-50/40 dark:bg-[#FF5F15]/5" : ""}${dropLine(i)}`}
                    {...dropHandlers(i)}
                    data-testid={`quote-item-row-${i}`}>
                  <td className="pl-2 cursor-grab text-zinc-300 dark:text-zinc-600"><GripVertical className="w-3.5 h-3.5" /></td>
                  <td className="px-1.5 py-1.5 font-mono text-xs text-zinc-400">{n}</td>
                  <td className="px-1.5 py-1.5 align-top">
                    <AutoGrowTextarea value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })}
                      placeholder="Description" className="w-full font-medium text-sm px-2 py-1.5 rounded-sm border-zinc-200 dark:border-white/10 dark:bg-white/5 min-h-9" data-testid={`item-desc-${i}`} disabled={!editable} />
                    {it.detail && <div className="text-[11px] text-zinc-400 mt-0.5 px-1.5 whitespace-pre-wrap break-words">{it.detail}</div>}
                  </td>
                  <td className="px-1.5 py-1.5 align-top"><Input type="number" step="0.01" value={it.qty ?? ""} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(i, { qty: e.target.value })} className={`${cellIn} w-full text-right`} data-testid={`item-qty-${i}`} disabled={!editable} /></td>
                  <td className="px-1.5 py-1.5 align-top"><Input value={it.unit || ""} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(i, { unit: e.target.value })} placeholder="ea" className={`${cellIn} w-full`} disabled={!editable} /></td>
                  <td className="px-1.5 py-1.5 align-top"><Input type="number" step="0.01" value={it.unit_price ?? ""} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(i, { unit_price: e.target.value })} className={`${cellIn} w-full text-right`} data-testid={`item-price-${i}`} disabled={!editable} /></td>
                  <td className="px-1.5 py-1.5 align-top"><Input type="number" step="1" min="0" max="100" value={it.discount_pct ?? ""} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(i, { discount_pct: e.target.value })} className={`${cellIn} w-full text-right`} data-testid={`item-disc-${i}`} disabled={!editable} /></td>
                  <td className="px-1.5 py-2.5 text-right font-mono font-bold text-sm align-top" data-testid={`item-total-${i}`}>{nzd(lineTotal(it))}</td>
                  <td className="pr-2 text-right align-top pt-1.5">
                    {editable && <RowMenu i={i} onEdit={() => setExpandedRow(expanded ? null : i)} onDup={() => duplicateItem(i)} onUp={() => moveItem(i, -1)} onDown={() => moveItem(i, 1)} onDel={() => removeItem(i)} />}
                  </td>
                </tr>
                {expanded && (
                  <tr className="bg-zinc-50 dark:bg-white/[0.03] border-b border-zinc-200 dark:border-white/10" data-testid={`item-expanded-${i}`}>
                    <td />
                    <td colSpan={8} className="px-3 py-3">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <Label className={labelCls}>Detailed description <span className="normal-case text-zinc-400">(shown to customer)</span></Label>
                          <Textarea rows={2} value={it.detail || ""} onChange={(e) => updateItem(i, { detail: e.target.value })} className="mt-1 rounded-sm text-xs dark:bg-white/5" data-testid={`item-detail-${i}`} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className={labelCls}>Cost code</Label>
                            <Input value={it.cost_code || ""} onChange={(e) => updateItem(i, { cost_code: e.target.value })} className={`${cellIn} mt-1 w-full`} data-testid={`item-costcode-${i}`} />
                          </div>
                          <div>
                            <Label className={labelCls}>Category</Label>
                            <Select value={it.category || "none"} onValueChange={(v) => updateItem(i, { category: v === "none" ? "" : v })}>
                              <SelectTrigger className={`${cellIn} mt-1 w-full`} data-testid={`item-category-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                <SelectItem value="labour">Labour</SelectItem>
                                <SelectItem value="material">Material</SelectItem>
                                <SelectItem value="plant">Plant / Equipment</SelectItem>
                                <SelectItem value="service">Service</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {[["labour_cost", "Labour cost"], ["material_cost", "Material cost"], ["plant_cost", "Plant cost"]].map(([k, label]) => (
                            <div key={k}>
                              <Label className={labelCls}>{label}</Label>
                              <Input type="number" step="0.01" value={it[k] ?? ""} onChange={(e) => updateItem(i, { [k]: e.target.value })} className={`${cellIn} mt-1 w-full text-right`} data-testid={`item-${k}-${i}`} />
                            </div>
                          ))}
                        </div>
                        <div>
                          <Label className={labelCls}>Internal notes <span className="normal-case text-red-500/80">— never shown to the customer</span></Label>
                          <Textarea rows={2} value={it.internal_note || ""} onChange={(e) => updateItem(i, { internal_note: e.target.value })} className="mt-1 rounded-sm text-xs dark:bg-white/5" data-testid={`item-internal-note-${i}`} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </RowGroup>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
const RowGroup = ({ children }) => <>{children}</>;

function RowMenu({ i, isSection, onEdit, onDup, onUp, onDown, onDel }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1 text-zinc-400 hover:text-zinc-800 dark:hover:text-white" data-testid={`item-menu-${i}`}>
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm">
        {!isSection && <DropdownMenuItem onClick={onEdit} data-testid={`item-menu-edit-${i}`}>Edit</DropdownMenuItem>}
        <DropdownMenuItem onClick={onDup}>Duplicate</DropdownMenuItem>
        <DropdownMenuItem onClick={onUp}>Move Up</DropdownMenuItem>
        <DropdownMenuItem onClick={onDown}>Move Down</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDel} className="text-red-600" data-testid={`item-menu-delete-${i}`}>Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ==================== LINKED JOB ====================
export function LinkedJobSection({ q, patch, jobs }) {
  return (
    <SectionCard title="Linked Job (Optional)" icon={Briefcase} testid="quote-linked-job">
      <p className="text-xs text-zinc-500 mb-3">Link this quote to an existing job or opportunity.</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Select value={q.job_id || "none"} onValueChange={(v) => patch({ job_id: v === "none" ? null : v })}>
          <SelectTrigger className={`${inputCls} w-full sm:w-80`} data-testid="quote-job-select"><SelectValue placeholder="Search jobs…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none"><span className="italic text-zinc-500">No linked job</span></SelectItem>
            {jobs.map((j) => (
              <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} – ` : ""}{j.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <Switch checked={!!q.auto_create_job} onCheckedChange={(v) => patch({ auto_create_job: v })} className="data-[state=checked]:bg-[#FF5F15]" data-testid="quote-auto-job-toggle" />
          <span className="text-xs font-medium">Create job automatically when quote is accepted</span>
        </label>
      </div>
    </SectionCard>
  );
}

// ==================== ATTACHMENTS ====================
export function AttachmentsSection({ q, setQ, uploadFiles }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
  return (
    <SectionCard title="Attachments" icon={Paperclip} testid="quote-attachments">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFiles(Array.from(e.dataTransfer.files || [])); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? "border-[#FF5F15] bg-orange-50/40 dark:bg-[#FF5F15]/5" : "border-zinc-300 dark:border-white/15 hover:border-[#FF5F15]"}`}
        data-testid="quote-attach-dropzone">
        <UploadCloud className="w-6 h-6 text-[#FF5F15] mx-auto mb-1.5" />
        <div className="text-sm font-bold">Drag files here or click to upload</div>
        <div className="text-[11px] text-zinc-400 mt-0.5">Supported: PDF, DOC, DWG, XLSX and images (max 15MB)</div>
        <input ref={inputRef} type="file" multiple className="hidden"
          accept=".pdf,.doc,.docx,.dwg,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic"
          onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
          data-testid="quote-attach-input" />
      </div>
      {(q.attachments || []).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {q.attachments.map((f, i) => (
            <div key={i} className="flex items-center gap-2.5 border border-zinc-200 dark:border-white/10 px-3 py-2" data-testid={`quote-attachment-${i}`}>
              <FileText className="w-4 h-4 text-[#FF5F15] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold truncate">{f.name}</div>
                <div className="text-[10px] text-zinc-400">{fmtSize(f.size || 0)}</div>
              </div>
              <button onClick={() => setQ((prev) => ({ ...prev, attachments: prev.attachments.filter((_, x) => x !== i) }))}
                className="text-zinc-400 hover:text-red-600" data-testid={`quote-attachment-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ==================== NOTES (rich-lite) ====================
export function NotesSection({ q, patch }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (q.notes_html || "") && document.activeElement !== ref.current) {
      ref.current.innerHTML = q.notes_html || (q.notes ? q.notes.replace(/\n/g, "<br/>") : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.id]);
  const exec = (cmd, arg) => { document.execCommand(cmd, false, arg); ref.current?.focus(); sync(); };
  const sync = () => {
    const html = ref.current?.innerHTML || "";
    const text = ref.current?.innerText || "";
    patch({ notes: text.trim(), settings: { ...(q.settings || {}), notes_html: html } });
  };
  const tools = [
    { icon: Bold, cmd: "bold", label: "Bold" }, { icon: Italic, cmd: "italic", label: "Italic" },
    { icon: List, cmd: "insertUnorderedList", label: "Bullet list" },
    { icon: ListOrdered, cmd: "insertOrderedList", label: "Numbered list" },
  ];
  return (
    <SectionCard title="Notes to Customer" icon={StickyNote} testid="quote-notes"
      right={<span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Appears on the quote</span>}>
      <div className="flex items-center gap-1 mb-2">
        {tools.map((t) => (
          <button key={t.cmd} title={t.label} onMouseDown={(e) => { e.preventDefault(); exec(t.cmd); }}
            className="p-1.5 border border-zinc-200 dark:border-white/10 text-zinc-500 hover:text-[#FF5F15] hover:border-[#FF5F15]"
            data-testid={`notes-tool-${t.cmd}`}>
            <t.icon className="w-3.5 h-3.5" />
          </button>
        ))}
        <button title="Link" onMouseDown={(e) => { e.preventDefault(); const u = window.prompt("Link URL:"); if (u) exec("createLink", u); }}
          className="p-1.5 border border-zinc-200 dark:border-white/10 text-zinc-500 hover:text-[#FF5F15] hover:border-[#FF5F15]" data-testid="notes-tool-link">
          <Link2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={sync} onBlur={sync}
        className="min-h-[90px] border border-zinc-300 dark:border-white/15 dark:bg-white/5 rounded-sm p-3 text-sm focus:outline-none focus:border-[#FF5F15] prose-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        data-placeholder="Add any additional information for your customer."
        data-testid="quote-notes-editor" />
    </SectionCard>
  );
}

// ==================== ASSUMPTIONS & EXCLUSIONS ====================
export function AssumptionsExclusionsSection({ q, patch }) {
  return (
    <SectionCard title="Assumptions & Exclusions" icon={ScrollText} testid="quote-assumptions-section"
      right={<span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Only shown if filled in</span>}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className={labelCls}>Assumptions</Label>
          <Textarea value={q.assumptions || ""} onChange={(e) => patch({ assumptions: e.target.value })} rows={4}
            placeholder={"- Clear site access available\n- Excavated spoil can remain onsite"}
            className="mt-1.5 rounded-sm border-zinc-300 dark:border-white/15 dark:bg-white/5 text-sm" data-testid="quote-assumptions" />
        </div>
        <div>
          <Label className={labelCls}>Exclusions</Label>
          <Textarea value={q.exclusions || ""} onChange={(e) => patch({ exclusions: e.target.value })} rows={4}
            placeholder={"- Traffic management\n- Rock excavation\n- Dewatering"}
            className="mt-1.5 rounded-sm border-zinc-300 dark:border-white/15 dark:bg-white/5 text-sm" data-testid="quote-exclusions" />
        </div>
      </div>
      <p className="text-[10px] text-zinc-400 mt-2">Start a line with &ldquo;-&rdquo; for a bullet point on the customer proposal.</p>
    </SectionCard>
  );
}

// ==================== TERMS ====================
const PAYMENT_TERMS = ["Due on Receipt", "7 Days", "14 Days", "20 Days", "30 Days", "20th of following month", "Custom"];
export function TermsSection({ q, patch }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/quote-templates", { params: { kind: "terms" } }).then((r) => setTemplates(r.data || [])).catch(() => {}); }, []);
  const saveTemplate = async () => {
    const name = window.prompt("Save current terms as template — name:");
    if (!name || !(q.terms || "").trim()) return;
    try {
      const { data } = await api.post("/quote-templates", { kind: "terms", name, data: { terms: q.terms, payment_terms: q.payment_terms } });
      setTemplates((p) => [data, ...p]);
      toast.success("Terms template saved");
    } catch { toast.error("Save failed"); }
  };
  return (
    <SectionCard title="Terms & Conditions" icon={ScrollText} testid="quote-terms-section"
      right={
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid="terms-load-template">Load Template</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              {templates.length === 0 && <DropdownMenuItem disabled>No saved templates</DropdownMenuItem>}
              {templates.map((t) => (
                <DropdownMenuItem key={t.id} onClick={() => patch({ terms: t.data?.terms || "", payment_terms: t.data?.payment_terms || q.payment_terms })} data-testid={`terms-template-${t.id}`}>{t.name}</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={saveTemplate} data-testid="terms-save-template">+ Save current as template</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className={labelCls}>Payment Terms</Label>
          <Select value={PAYMENT_TERMS.includes(q.payment_terms) ? q.payment_terms : "Custom"}
            onValueChange={(v) => patch({ payment_terms: v === "Custom" ? (q.payment_terms || "") : v })}>
            <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="quote-payment-terms"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYMENT_TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          {!PAYMENT_TERMS.slice(0, -1).includes(q.payment_terms) && (
            <Input value={q.payment_terms || ""} onChange={(e) => patch({ payment_terms: e.target.value })} placeholder="Custom payment terms" className={`${inputCls} mt-2`} data-testid="quote-payment-terms-custom" />
          )}
        </div>
      </div>
      <Label className={labelCls}>Quote Terms</Label>
      <Textarea rows={4} value={q.terms || ""} onChange={(e) => patch({ terms: e.target.value })}
        placeholder="This quote is valid for 30 days from the issue date…"
        className="mt-1.5 rounded-sm text-sm dark:bg-white/5" data-testid="quote-terms-text" />
    </SectionCard>
  );
}

// ==================== ACTIVITY ====================
export function ActivitySection({ q, setQ, noteUrl }) {
  const [note, setNote] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const entries = (q.activity || []).slice().reverse();
  const stamps = [
    ["Created", q.created_at], ["Sent", q.sent_at], ["Viewed", q.viewed_at],
    ["Accepted", q.accepted_at], ["Declined", q.declined_at], ["Paid", q.paid_at],
  ].filter(([, v]) => v);
  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      const { data } = await api.post(noteUrl || `/quotes/${q.id}/note`, { text: note });
      setQ((prev) => ({ ...prev, activity: [...(prev.activity || []), data] }));
      setNote(""); setShowAdd(false);
    } catch { toast.error("Could not add note"); }
  };
  return (
    <SectionCard title="Activity" icon={ActivityIcon} testid="quote-activity"
      right={
        <button onClick={() => setShowAdd((s) => !s)} className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid="quote-add-note">
          + Add Internal Note
        </button>
      }>
      {stamps.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {stamps.map(([label, v]) => (
            <span key={label} className="text-[10px] font-mono px-2 py-0.5 bg-zinc-100 dark:bg-white/5 text-zinc-500">
              {label}: {new Date(v).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}
            </span>
          ))}
        </div>
      )}
      {showAdd && (
        <form onSubmit={addNote} className="flex gap-2 mb-3">
          <Input autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note — never visible to customers" className={`${inputCls} flex-1`} data-testid="quote-note-input" />
          <Button type="submit" size="sm" className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase" data-testid="quote-note-save">Add</Button>
        </form>
      )}
      <div className="space-y-0">
        {entries.length === 0 && <div className="text-xs text-zinc-400 italic">No activity yet.</div>}
        {entries.map((a, i) => (
          <div key={i} className="flex gap-3 pb-3 relative" data-testid={`activity-entry-${i}`}>
            <div className="flex flex-col items-center">
              <div className={`w-2 h-2 rounded-full mt-1.5 ${a.kind === "note" ? "bg-sky-400" : "bg-[#FF5F15]"}`} />
              {i < entries.length - 1 && <div className="w-px flex-1 bg-zinc-200 dark:bg-white/10" />}
            </div>
            <div className="pb-1 min-w-0">
              <div className="text-xs font-bold">{a.text}{a.kind === "note" && <span className="ml-1.5 text-[9px] font-bold uppercase text-sky-500">internal</span>}</div>
              <div className="text-[10px] text-zinc-400">by {a.by} · {new Date(a.at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}</div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ==================== SIDEBAR PANELS ====================
export function SummaryPanel({ totals, q, onPreview, onSend }) {
  return (
    <section className="border-2 border-[#FF5F15]/40 bg-white dark:bg-[#0F141C]" data-testid="quote-summary-panel">
      <div className="px-4 py-2.5 border-b border-zinc-100 dark:border-white/5 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
        <CircleDollarSign className="w-3.5 h-3.5 text-[#FF5F15]" /> Quote Summary
      </div>
      <div className="p-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-zinc-500">Subtotal{q.gst_enabled === false ? "" : ` (${q.gst_inclusive ? "incl." : "excl."} GST)`}</span><span className="font-mono" data-testid="sum-subtotal">{nzd(totals.subtotal)}</span></div>
        {totals.discount > 0 && (
          <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Discount</span><span className="font-mono" data-testid="sum-discount">-{nzd(totals.discount)}</span></div>
        )}
        {q.gst_enabled !== false && !q.gst_inclusive && totals.discount > 0 && <div className="flex justify-between"><span className="text-zinc-500">After discount (excl. GST)</span><span className="font-mono" data-testid="sum-net">{nzd(totals.net)}</span></div>}
        {q.gst_enabled !== false && <div className="flex justify-between"><span className="text-zinc-500">{q.gst_inclusive ? "GST included (15%)" : "GST (15%)"}</span><span className="font-mono" data-testid="sum-gst">{nzd(totals.gst)}</span></div>}
        {q.gst_enabled !== false && q.gst_inclusive && <div className="flex justify-between"><span className="text-zinc-500">{totals.discount > 0 ? "After discount" : "Subtotal"} (excl. GST)</span><span className="font-mono" data-testid="sum-net">{nzd(totals.net)}</span></div>}
        <div className="border-t-2 border-zinc-200 dark:border-white/10 pt-3 mt-3 flex items-end justify-between">
          <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">Total</span>
          <div className="text-right">
            <div className="text-2xl font-black font-mono text-[#FF5F15] leading-none" data-testid="sum-total">{nzd(totals.total)}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mt-1">NZD{q.gst_enabled === false ? "" : " · INCLUDES GST"}</div>
          </div>
        </div>
        <div className="pt-3 space-y-2">
          <Button variant="outline" onClick={onPreview} className="w-full rounded-sm h-9 text-[11px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="summary-preview-pdf">Preview Quote PDF</Button>
          <Button onClick={onSend} className="w-full rounded-sm h-10 bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="summary-send">
            <Send className="w-3.5 h-3.5 mr-2" /> Send Quote to Customer
          </Button>
        </div>
      </div>
    </section>
  );
}

export function DiscountPanel({ q, patch }) {
  const [open, setOpen] = useState(!!q.discount_value);
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]" data-testid="quote-discount-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2.5" data-testid="discount-toggle">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 flex items-center gap-2"><Percent className="w-3.5 h-3.5 text-[#FF5F15]" /> Discount</span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-100 dark:border-white/5 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>Discount Type</Label>
              <Select value={q.discount_type || "none"} onValueChange={(v) => patch({ discount_type: v === "none" ? null : v, ...(v === "none" ? { discount_value: null } : {}) })}>
                <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No discount</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Value {q.discount_type === "percentage" ? "(%)" : "($)"}</Label>
              <Input type="number" step="0.01" min="0" value={q.discount_value ?? ""} disabled={!q.discount_type}
                onChange={(e) => patch({ discount_value: e.target.value })}
                className={`${inputCls} mt-1.5 text-right`} data-testid="discount-value" />
            </div>
          </div>
          <div>
            <Label className={labelCls}>Reason (optional)</Label>
            <Input value={q.discount_reason || ""} onChange={(e) => patch({ discount_reason: e.target.value })} placeholder="e.g. Volume Discount" className={`${inputCls} mt-1.5`} data-testid="discount-reason" />
          </div>
        </div>
      )}
    </section>
  );
}

export function TaxPanel({ q, patch }) {
  const noGst = q.gst_enabled === false;
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] p-4" data-testid="quote-tax-panel">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 mb-3">Tax / GST</div>
      <label className="flex items-center gap-2.5 cursor-pointer select-none mb-3">
        <Checkbox checked={noGst} onCheckedChange={(v) => patch({ gst_enabled: !v })} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" data-testid="quote-no-gst-checkbox" />
        <span className="text-xs font-medium">No GST (not GST registered)</span>
      </label>
      {noGst ? (
        <p className="text-[10px] text-zinc-400 mt-2">This quote will not show or add any GST.</p>
      ) : (
      <>
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-zinc-500">GST Rate</span>
        <span className="font-mono font-bold">15%</span>
      </div>
      <label className="flex items-center gap-2.5 cursor-pointer select-none">
        <Checkbox checked={!!q.gst_inclusive} onCheckedChange={(v) => patch({ gst_inclusive: !!v })} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" data-testid="gst-inclusive-checkbox" />
        <span className="text-xs font-medium">Prices Include GST</span>
      </label>
      <p className="text-[10px] text-zinc-400 mt-2">
        {q.gst_inclusive
          ? "Line prices already include GST. No additional GST will be added to the quote total."
          : "GST will be added to the line prices shown above."}
      </p>
      </>
      )}
    </section>
  );
}

const SETTING_ROWS = [
  ["show_item_images", "Show Item Images"],
  ["show_descriptions", "Show Item Descriptions"],
  ["show_quantities", "Show Quantities"],
  ["show_unit_prices", "Show Unit Prices"],
  ["show_section_subtotals", "Show Section Subtotals"],
  ["show_discount", "Show Discount"],
];
const PRICING_DISPLAY_OPTIONS = [
  ["default", "Use company default"],
  ["detailed", "Detailed (qty, unit, rate, total)"],
  ["standard", "Standard (description + total)"],
  ["section_summary", "Section summary"],
  ["lump_sum", "Lump sum only"],
];
const QUOTE_ACCENTS = [["orange", "#FF5F15"], ["blue", "#0284C7"], ["green", "#059669"], ["purple", "#7C3AED"], ["grey", "#52525B"]];

export function SettingsPanel({ q, patch, rows = SETTING_ROWS, title = "Customer Quote Settings", accentLabel = "Quote Accent Colour", extra = null }) {
  const s = q.settings || {};
  const [orgDefaults, setOrgDefaults] = useState({});
  const isQuote = rows === SETTING_ROWS;
  useEffect(() => {
    if (!isQuote) return;
    api.get("/org/branding").then(({ data }) => setOrgDefaults(data?.quote_branding || {})).catch(() => {});
  }, [isQuote]);
  const effective = (k) => (k in s ? s[k] !== false : orgDefaults[k] !== false);
  const accentHex = (q.accent_color || "").startsWith("#")
    ? q.accent_color
    : (QUOTE_ACCENTS.find(([key]) => key === (q.accent_color || "orange")) || QUOTE_ACCENTS[0])[1];
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] p-4" data-testid="quote-settings-panel">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 mb-3 flex items-center gap-2"><Settings2 className="w-3.5 h-3.5 text-[#FF5F15]" /> {title}</div>
      {isQuote && (
        <div className="mb-4">
          <div className={labelCls}>Customer Pricing Display</div>
          <Select value={s.pricing_display || "default"} onValueChange={(v) => patch({ settings: { ...s, pricing_display: v === "default" ? undefined : v } })}>
            <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="quote-pricing-display-override"><SelectValue placeholder="Use company default" /></SelectTrigger>
            <SelectContent>
              {PRICING_DISPLAY_OPTIONS.map(([k, label]) => (
                <SelectItem key={k} value={k} data-testid={`quote-pricing-display-${k}`}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-zinc-400 mt-1.5">How much pricing detail this customer sees on their proposal.</p>
        </div>
      )}
      <div className="space-y-2.5">
        {rows.map(([k, label]) => (
          <label key={k} className="flex items-center justify-between cursor-pointer select-none">
            <span className="text-xs">{label}</span>
            <Switch checked={isQuote ? effective(k) : !!s[k]}
              onCheckedChange={(v) => patch({ settings: { ...s, [k]: v } })}
              className="data-[state=checked]:bg-[#FF5F15] scale-90" data-testid={`setting-${k}`} />
          </label>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-white/5">
        <div className={labelCls}>{accentLabel}</div>
        <div className="flex items-center gap-2 mt-2">
          {QUOTE_ACCENTS.map(([key, hex]) => (
            <button key={key} onClick={() => patch({ accent_color: key })} title={key}
              className={`w-6 h-6 rounded-full ${(q.accent_color || "orange") === key ? "ring-2 ring-offset-2 ring-[#FF5F15] dark:ring-offset-[#0F141C]" : "opacity-60 hover:opacity-100"}`}
              style={{ background: hex }}
              data-testid={`accent-${key}`} />
          ))}
          <input type="color" value={accentHex} onChange={(e) => patch({ accent_color: e.target.value.toUpperCase() })}
            className="w-7 h-7 rounded-sm border border-zinc-300 dark:border-white/15 bg-transparent cursor-pointer"
            title="Custom colour" data-testid="accent-custom" />
        </div>
        <p className="text-[10px] text-zinc-400 mt-2">Used on the customer proposal, PDF and email.</p>
      </div>
      {extra}
    </section>
  );
}

export function QuickActionsPanel({ onEstimate, onDuplicate, onTemplate, onImportQuote, actions: custom }) {
  const actions = custom || [
    { t: "Convert from Estimate", d: "Import line items from an existing estimate.", fn: onEstimate, id: "qa-estimate" },
    { t: "Duplicate Existing Quote", d: "Search another quote and duplicate it.", fn: onDuplicate, id: "qa-duplicate" },
    { t: "Save as Template", d: "Save the quote structure as a reusable template.", fn: onTemplate, id: "qa-template" },
    { t: "Import Previous Quote", d: "Search and import previous quotes.", fn: onImportQuote, id: "qa-import" },
  ];
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] p-4" data-testid="quote-quick-actions">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 mb-3 flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-[#FF5F15]" /> Quick Actions</div>
      <div className="space-y-1">
        {actions.map((a) => (
          <button key={a.id} onClick={a.fn} className="w-full text-left px-2.5 py-2 hover:bg-zinc-50 dark:hover:bg-white/5 border border-transparent hover:border-zinc-200 dark:hover:border-white/10 transition-colors" data-testid={a.id}>
            <div className="text-xs font-bold">{a.t}</div>
            <div className="text-[10px] text-zinc-400">{a.d}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ==================== ITEM LIBRARY DIALOG ====================
const LIB_CATS = [
  { key: "all", label: "⭐ Frequently Used", icon: Star },
  { key: "material", label: "📦 Materials", icon: Package },
  { key: "labour", label: "👷 Labour", icon: Hammer },
  { key: "plant", label: "🚜 Plant & Equipment", icon: Truck },
  { key: "service", label: "🔧 Services", icon: Wrench },
];
export function ItemLibraryDialog({ open, onClose, onAdd }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [picked, setPicked] = useState({});
  useEffect(() => {
    if (!open) return;
    setPicked({});
    api.get("/rate-library").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [open]);
  const matchCat = (it) => {
    if (cat === "all") return true;
    const c = (it.category || "").toLowerCase();
    if (cat === "material") return ["material", "materials", "concrete", "aggregate", "pipe", "drainage"].some((x) => c.includes(x));
    if (cat === "labour") return c.includes("labour") || c.includes("labor");
    if (cat === "plant") return ["plant", "equipment", "machine", "earthworks"].some((x) => c.includes(x));
    if (cat === "service") return c.includes("service");
    return true;
  };
  const filtered = items.filter((it) => (it.description || "").toLowerCase().includes(search.toLowerCase()) && matchCat(it));
  const count = Object.values(picked).filter(Boolean).length;
  const add = () => {
    const chosen = items.filter((it) => picked[it.id]).map((it) => ({
      description: it.description, qty: it.default_qty || 1, unit: it.unit || "ea",
      unit_price: it.unit_price || 0, discount_pct: 0, is_section: false,
      category: it.category || "", detail: "", internal_note: "",
    }));
    if (!chosen.length) return;
    onAdd(chosen);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] flex flex-col" data-testid="item-library-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Item Library</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input autoFocus placeholder="Search materials, labour, plant, services…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="library-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {LIB_CATS.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)}
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border ${cat === c.key ? "bg-[#FF5F15] text-white border-[#FF5F15]" : "border-zinc-300 dark:border-white/15 text-zinc-500 hover:border-[#FF5F15]"}`}
              data-testid={`library-cat-${c.key}`}>{c.label}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto border border-zinc-200 dark:border-white/10 divide-y divide-zinc-100 dark:divide-white/5 min-h-[200px]">
          {filtered.length === 0 && <div className="p-8 text-center text-sm text-zinc-400">Nothing in the library matches. Build your rate library under Rates.</div>}
          {filtered.map((it) => (
            <label key={it.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 dark:hover:bg-white/5 cursor-pointer" data-testid={`library-item-${it.id}`}>
              <Checkbox checked={!!picked[it.id]} onCheckedChange={(v) => setPicked((p) => ({ ...p, [it.id]: !!v }))} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{it.description}</div>
                <div className="text-[10px] text-zinc-400 uppercase">{it.category || "uncategorised"}</div>
              </div>
              <div className="font-mono text-xs font-bold shrink-0">{nzd(it.unit_price)} / {it.unit || "ea"}</div>
            </label>
          ))}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-zinc-500">{count} selected</span>
          <Button onClick={add} disabled={!count} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="library-add-selected">
            Add {count || ""} to Quote
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SEND DIALOG ====================
export function SendDialog({ open, onClose, q, saveNow, onSent }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setEmail(q.client_email || ""); }, [open, q.client_email]);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const priceError = quotePriceError(q.line_items);
      if (priceError) { toast.error(priceError); return; }
      if (!await saveNow()) return;
      const { data } = await api.post(`/quotes/${q.id}/send`, { to_email: email, message: message || undefined });
      onSent({ status: "sent", sent_at: new Date().toISOString(), ...(data || {}) });
      toast.success(`Quote sent to ${email}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="quote-send-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Send {q?.quote_number}</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <div>
            <Label className={labelCls}>Customer email</Label>
            <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="send-email-input" />
          </div>
          <div>
            <Label className={labelCls}>Message (optional)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A short note to go with the quote…" className="mt-1.5 rounded-sm text-sm" data-testid="send-message-input" />
          </div>
          <p className="text-[10px] text-zinc-400">The customer gets the PDF plus a secure link to view, accept or decline online.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm text-[11px] font-bold uppercase">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="send-confirm">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send Quote
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== IMPORT / DUPLICATE PICKER ====================
export function ImportDocDialog({ mode, onClose, onPick, currentId }) {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (!mode) return;
    setSearch("");
    api.get("/quotes", { params: { status: "all" } }).then((r) => {
      let list = (r.data || []).filter((d) => d.id !== currentId);
      if (mode === "estimate") list = list.filter((d) => d.doc_type === "estimate");
      setDocs(list);
    }).catch(() => setDocs([]));
  }, [mode, currentId]);
  const filtered = docs.filter((d) => `${d.quote_number} ${d.client_name} ${d.title || ""}`.toLowerCase().includes(search.toLowerCase()));
  const titles = { estimate: "Import from Estimate", quote: "Import Previous Quote", duplicate: "Duplicate Existing Quote" };
  return (
    <Dialog open={!!mode} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="import-doc-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{titles[mode] || "Import"}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input autoFocus placeholder="Search by number, customer, title…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="import-search" />
        </div>
        <div className="max-h-72 overflow-y-auto border border-zinc-200 dark:border-white/10 divide-y divide-zinc-100 dark:divide-white/5">
          {filtered.length === 0 && <div className="p-6 text-center text-sm text-zinc-400">{mode === "estimate" ? "No estimates found." : "No quotes found."}</div>}
          {filtered.map((d) => (
            <button key={d.id} onClick={() => onPick(d)} className="w-full text-left px-3 py-2 hover:bg-zinc-50 dark:hover:bg-white/5 flex items-center gap-3" data-testid={`import-doc-${d.id}`}>
              <span className="font-mono text-xs font-bold">{d.quote_number}</span>
              <span className="text-sm truncate flex-1">{d.title || d.client_name}</span>
              <span className="font-mono text-xs">{nzd(d.total)}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
